import { formatMoneyColumnHeader } from "@common/helpers/number";
import { NumberFormatValues } from "@common/schemas/domains/reportSettings/number-format-setting.schema";
import {
    SchedulesReportLayout,
    SchedulesReportRows,
} from "@common/types/reports/schedules-report";
import { ReportPeriodWithLabel } from "@common/types/reports/types";
import { AMOUNT_COLUMN_SIZE } from "@design-system/ViewTable/DefaultColumnSizes";
import { ColumnGroupConfig } from "@lib/views";
import {
    SchedulesWaterfallSummaryField,
    getWaterfallSummaryValueIndex,
} from "./schedulesReportViewBuilder";
import { SchedulesColumnConfig } from "./schedulesReportViewTypes";
import { makeSchedulesReportSummaryCell } from "./SchedulesReportValueCell";

export const SUMMARY_BOOKED_GROUP_ID = "summary-booked-group";
export const SUMMARY_RECOGNIZED_GROUP_ID = "summary-recognized-group";
const SUMMARY_AS_OF_GROUP_ID = "summary-as-of-group";

export const SUMMARY_COLUMN_IDS: Record<
    SchedulesWaterfallSummaryField,
    string
> = {
    booked: "summary-booked",
    recognized: "summary-recognized",
    remaining: "summary-remaining",
};

const SUMMARY_COLUMNS: Array<{
    field: SchedulesWaterfallSummaryField;
    name: string;
    header: string;
    groupId: string;
}> = [
    {
        field: "booked",
        name: "Booked total",
        header: "Total",
        groupId: SUMMARY_BOOKED_GROUP_ID,
    },
    {
        field: "recognized",
        name: "Recognized to date",
        header: "Recognized",
        groupId: SUMMARY_AS_OF_GROUP_ID,
    },
    {
        field: "remaining",
        name: "Remaining balance",
        header: "Remaining",
        groupId: SUMMARY_AS_OF_GROUP_ID,
    },
];

export function showsWaterfallSummary(params: {
    layout: SchedulesReportLayout;
    rows?: SchedulesReportRows;
}): boolean {
    return (
        params.layout === SchedulesReportLayout.waterfall &&
        params.rows === SchedulesReportRows.startMonthSummary
    );
}

export function buildWaterfallSummaryColumns(params: {
    periodCount: number;
    numberFormat?: NumberFormatValues;
}): Record<string, SchedulesColumnConfig> {
    return Object.fromEntries(
        SUMMARY_COLUMNS.map(({ field, name, header, groupId }) => [
            SUMMARY_COLUMN_IDS[field],
            {
                name,
                header: formatMoneyColumnHeader(header, params.numberFormat),
                hiddenFromSettings: true,
                accessorKey: "values",
                cell: makeSchedulesReportSummaryCell(
                    getWaterfallSummaryValueIndex({
                        periodCount: params.periodCount,
                        field,
                    }),
                ),
                ...AMOUNT_COLUMN_SIZE,
                groupId,
            },
        ]),
    );
}

export function buildWaterfallSummaryGroups(
    periods: ReportPeriodWithLabel[],
): Record<string, ColumnGroupConfig> {
    const lastPeriod = periods.at(-1);
    return {
        [SUMMARY_BOOKED_GROUP_ID]: { header: "Booked" },
        [SUMMARY_RECOGNIZED_GROUP_ID]: { header: "Recognized" },
        [SUMMARY_AS_OF_GROUP_ID]: {
            header: lastPeriod ? `As of ${lastPeriod.label}` : "As of",
        },
    };
}
